import { useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  Dropdown,
  SimpleCheckbox,
  Text,
  TextButton,
  TextInput,
} from '@opendoor/bricks-next';
import { globalObservability } from '@opendoor/observability/slim';
import { OdProtosSellReceptionData_SellerInput_Channel } from '__generated__/athena';
import { useRouter } from 'next/router';

import {
  ADDRESS_INVALID_RESULT,
  gateProceedToOnboardingFlow,
  SelectedAddress,
  useAddressValidationContext,
} from '../../helpers/AddressValidationContext';
import { useObservability } from '../../helpers/observability';
import { standardizeAddress } from '../api';
import { AddressSearchLocation, TrackingTaxonomy } from '../globals';

const Sentry = globalObservability.getSentryClient();

type RelationshipValue = 'Self' | 'Agent' | 'AgentAndOwner' | 'other';

export const extractStreetNumber = (address: SelectedAddress | null) => {
  const regex = /\b\d+[A-Za-z]*(?:-\d+[A-Za-z]*)?\b/;
  const matches = address?.street1?.match(regex);
  return matches ? matches[0] : null;
};

export const addressNotFound = (errorData: string | { message: string }) => {
  const msg = typeof errorData == 'string' ? errorData : errorData.message;
  return msg.indexOf('NOT_FOUND') === 2;
};

export const AddressConfirmationModal = ({
  isOpen,
  setIsOpen,
  partnerReferralId,
  initialAddress,
  pageSetPath,
  trackingKeywords,
  channel,
  inputLocation,
  trackingTaxonomy,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  partnerReferralId?: string;
  initialAddress: SelectedAddress;
  pageSetPath?: string;
  trackingKeywords?: string;
  channel?: OdProtosSellReceptionData_SellerInput_Channel;
  inputLocation: AddressSearchLocation;
  trackingTaxonomy: TrackingTaxonomy;
}) => {
  const router = useRouter();
  const { trackEvent } = useObservability();
  const { addressResults, setValidatedAddress, validatedAddress } = useAddressValidationContext();
  const [address, setAddress] = useState<SelectedAddress>({ ...initialAddress });
  const [unitNumberErrorText, setUnitNumberErrorText] = useState<string>('');
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [noUnitChecked, setNoUnitChecked] = useState<boolean>(false);
  const [confirmSameUnitStreet, setConfirmSameUnitStreet] = useState<boolean>(false);
  const [relationshipValue, setRelationshipValue] = useState<RelationshipValue | ''>('');
  const [relationshipError, setRelationshipError] = useState<string>('');
  const isAddressUnitNumberMissing = addressResults?.includes('ADDRESS_MISSING_SUB_PREMISE');
  const streetNumber = extractStreetNumber(initialAddress);

  const validateUnitInput = async (): Promise<SelectedAddress | undefined> => {
    try {
      const response = await standardizeAddress(address);
      if (
        !response ||
        !response.data ||
        (response.errors && !addressNotFound(response.errors[0]))
      ) {
        setUnitNumberErrorText('An unexpected error occurred. Please try again.');
        return;
      }
      const casaAddress = response.data?.casa.findByFields?.address;
      if (
        casaAddress?.result.includes(ADDRESS_INVALID_RESULT) ||
        (response.errors && addressNotFound(response.errors[0]))
      ) {
        setUnitNumberErrorText('Unit number not found or entered incorrectly');
        return;
      }
      return {
        street1: casaAddress.street,
        city: casaAddress.city,
        state: casaAddress.state,
        postal_code: casaAddress.postalCode,
        unit: casaAddress.unitNumber,
        id: casaAddress.uuid,
      };
    } catch (error) {
      Sentry.captureException?.(error);
      setUnitNumberErrorText('An unexpected error occurred. Please try again.');
      return;
    }
  };

  const routeToManualAddressPage = () => {
    router.push({
      pathname: '/manual-address-entry',
      query: {
        address: JSON.stringify(validatedAddress),
        ...(trackingKeywords != null && { trackingKeywords }),
        ...(pageSetPath != null && { pageSetPath }),
        ...(partnerReferralId != null && { partnerReferralId }),
        ...(channel != null && { channel }),
      },
    });
  };

  const proceedWithAddress = async (addressToUse: SelectedAddress) => {
    setValidatedAddress(addressToUse);

    const relationshipAnswers = relationshipValue
      ? { 'seller.relation_to_owner': relationshipValue }
      : undefined;

    try {
      await gateProceedToOnboardingFlow({
        address: addressToUse,
        product: 'SELL_DIRECT',
        trackEvent,
        partnerReferralId,
        addressUUID: addressToUse.id,
        pageSetPath,
        trackingKeywords,
        channel,
        inputLocation,
        trackingTaxonomy,
        relationshipAnswers,
      });
    } catch (error) {
      Sentry.captureException?.(error);
      setUnitNumberErrorText('An unexpected error occurred. Please try again.');
    } finally {
      setIsConfirmLoading(false);
    }
  };

  const validateRelationship = (): boolean => {
    if (!relationshipValue) {
      setRelationshipError('Please select an option');
      return false;
    }
    setRelationshipError('');
    return true;
  };

  const handleRelationshipSelect = (value: RelationshipValue | '') => {
    setRelationshipValue(value);
    setRelationshipError('');
  };

  const handleNoUnitToggle = () => {
    const newChecked = !noUnitChecked;
    setNoUnitChecked(newChecked);
    if (newChecked) {
      setAddress((prev) => ({ ...prev, unit: '' }));
      setUnitNumberErrorText('');
    }
  };

  const handleConfirm = async () => {
    setIsConfirmLoading(true);

    if (!validateRelationship()) {
      setIsConfirmLoading(false);
      return;
    }

    if (isAddressUnitNumberMissing && !noUnitChecked) {
      if (!address?.unit) {
        setUnitNumberErrorText('Enter a unit number or check "No unit number" below');
        setIsConfirmLoading(false);
        return;
      }

      const validatedAddr = await validateUnitInput();
      if (validatedAddr) {
        await proceedWithAddress(validatedAddr);
      } else {
        setIsConfirmLoading(false);
      }
    } else {
      await proceedWithAddress(address);
    }
  };

  const handleUnitNumberChange = (input: string) => {
    setConfirmSameUnitStreet(false);
    if (address) {
      setAddress({ ...address, unit: input });
    }
    const validUnitNumberRegex = /^[A-Za-z0-9\s-#]+$/;
    if (input && !validUnitNumberRegex.test(input)) {
      setUnitNumberErrorText('Unit numbers can’t include special characters.');
    } else if (input === streetNumber) {
      setUnitNumberErrorText(
        'If street and unit number are the same, tap "Same street and unit" below.',
      );
    } else {
      setUnitNumberErrorText('');
    }
  };

  return (
    <Dialog isOpen={isOpen} dismissable overlayColor="dark" setIsOpen={setIsOpen}>
      <Box width={600} maxHeight="90vh" overflow="scroll" $smallerThanLG={{ width: '100%' }}>
        <Dialog.Content
          title={
            isAddressUnitNumberMissing ? 'Is there a unit?' : 'Is this the correct home address?'
          }
          titleProps={{ fontSize: 32, lineHeight: 40 }}
          description=""
        >
          <Box flexDirection="column" gap={24}>
            <Box flexDirection="column" gap={32} $smallerThanLG={{ gap: 24 }}>
              <Box
                flexDirection="row"
                width="100%"
                justifyContent="space-between"
                alignItems="center"
                p="$12x"
                backgroundColor="$backgroundSecondary"
                borderRadius="$12x"
                $smallerThanLG={{ padding: '$8x', borderRadius: '$8x' }}
              >
                <Box flexDirection="column" width="60%" $smallerThanLG={{ width: '70%' }}>
                  <Text
                    tag="p"
                    typography="$bodyXlarge"
                    $smallerThanLG={{ typography: '$bodyLarge' }}
                  >
                    {address?.street1}
                    {address?.unit ? `, #${address.unit}` : ''}
                  </Text>
                  <Text
                    tag="p"
                    typography="$bodyXlarge"
                    $smallerThanLG={{ typography: '$bodyLarge' }}
                  >
                    {address?.city}, {address?.state} {address?.postal_code}
                  </Text>
                </Box>
                <TextButton
                  analyticsName="cosmos-edit-address"
                  label="Edit"
                  onPress={routeToManualAddressPage}
                  size="medium"
                  variant="primary"
                />
              </Box>

              {isAddressUnitNumberMissing && (
                <Box flexDirection="column" gap={6}>
                  <TextInput
                    id="cosmos-address-confirmation-input"
                    size="medium"
                    title="Add unit number, apt, floor, etc."
                    required
                    placeholder="#1234"
                    errorText={unitNumberErrorText}
                    onChangeText={handleUnitNumberChange}
                    disabled={noUnitChecked}
                    value={address?.unit ?? ''}
                  />

                  {address?.unit === streetNumber && !confirmSameUnitStreet && !noUnitChecked && (
                    <TextButton
                      variant="primary"
                      id="same-street-unit"
                      label="Same street and unit"
                      size="medium"
                      analyticsName="cosmos-same-street-unit"
                      trackImpression
                      onPress={() => {
                        setUnitNumberErrorText('');
                        setConfirmSameUnitStreet(true);
                      }}
                    />
                  )}

                  <Box
                    aria-label="No unit number"
                    analyticsName="address-confirmation-no-unit-checkbox"
                    flexDirection="row"
                    alignItems="center"
                  >
                    <SimpleCheckbox
                      analyticsName="address-confirmation-no-unit"
                      size="medium"
                      ariaLabel="No unit number"
                      value="no-unit"
                      checked={noUnitChecked}
                      onPress={handleNoUnitToggle}
                    />
                    <Text tag="span" typography="$bodyMedium" color="$contentSecondary">
                      No unit number
                    </Text>
                  </Box>
                </Box>
              )}

              <Dropdown
                analyticsName="address-confirmation-relationship-dropdown"
                id="address-confirmation-relationship"
                size="medium"
                title="Are you the owner of this home?"
                required
                placeholderOption={{
                  label: 'Select',
                }}
                options={[
                  {
                    value: 'Self',
                    label: 'Yes, I own this home',
                  },
                  {
                    value: 'Agent',
                    label: 'No, I’m an agent',
                  },
                  {
                    value: 'AgentAndOwner',
                    label: 'I’m an agent, and own this home',
                  },
                  {
                    value: 'other',
                    label: 'Other',
                  },
                ]}
                onValueChange={(value) => handleRelationshipSelect(value as RelationshipValue | '')}
                errorText={relationshipError || undefined}
              />
            </Box>

            <Box width="100%">
              <Button
                width="100%"
                analyticsName="address-confirmation-modal-confirm"
                label="Confirm"
                onPress={handleConfirm}
                size="medium"
                variant="primary"
                loading={isConfirmLoading}
                disabled={
                  !relationshipValue ||
                  !!relationshipError ||
                  !!unitNumberErrorText ||
                  (isAddressUnitNumberMissing && !address?.unit && !noUnitChecked)
                }
              />
            </Box>
          </Box>
        </Dialog.Content>
      </Box>
    </Dialog>
  );
};
